import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LockKeyhole, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { setToken } from "@/lib/auth";

/**
 * PIN entry dialog.
 *
 * Behaviour:
 *   - 4-digit numeric OTP (autofocus, keypad on mobile).
 *   - Submits on 4-digit fill; also on Enter.
 *   - Wrong PIN → shake, clear input, show attempts remaining.
 *   - Server 429 → locks the form and counts down `retry_after` seconds.
 *   - onSuccess() fires after a token is stored; onCancel() fires when
 *     the user closes without unlocking.
 *
 * The dialog is intentionally *modal* — the user can dismiss it
 * (which cancels any in-flight mutation the interceptor was replaying).
 */
export default function PinModal({ open, onSuccess, onCancel }) {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [messageKind, setMessageKind] = useState("info"); // 'info' | 'error'
  const [lockUntil, setLockUntil] = useState(0); // epoch ms
  const [now, setNow] = useState(Date.now());
  const shakeKeyRef = useRef(0);
  const submittedRef = useRef(false);

  const isLocked = lockUntil > now;
  const secondsLeft = isLocked ? Math.max(1, Math.ceil((lockUntil - now) / 1000)) : 0;

  // Reset every time the modal opens
  useEffect(() => {
    if (open) {
      setValue("");
      setSubmitting(false);
      setMessage("");
      setMessageKind("info");
      submittedRef.current = false;
    }
  }, [open]);

  // Ticking for the lockout countdown
  useEffect(() => {
    if (!open || !isLocked) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [open, isLocked]);

  const doSubmit = async (pin) => {
    if (submitting || isLocked) return;
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    setMessage("");
    try {
      const data = await api.verifyPin(pin);
      setToken(data.token);
      setSubmitting(false);
      onSuccess && onSuccess();
    } catch (e) {
      setSubmitting(false);
      submittedRef.current = false;
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;

      if (status === 429) {
        const retryAfter = (typeof detail === "object" && detail?.retry_after) || 30;
        setLockUntil(Date.now() + retryAfter * 1000);
        setValue("");
        setMessage(
          typeof detail === "object" && detail?.message
            ? detail.message
            : "Locked. Try again shortly.",
        );
        setMessageKind("error");
      } else if (status === 401) {
        // Shake + clear + show attempts
        shakeKeyRef.current += 1;
        setValue("");
        const remaining =
          typeof detail === "object" && detail?.attempts_remaining;
        setMessage(
          remaining != null
            ? `Wrong PIN — ${remaining} tries left`
            : "Wrong PIN.",
        );
        setMessageKind("error");
      } else {
        setMessage("Couldn't verify PIN. Check your connection.");
        setMessageKind("error");
      }
    }
  };

  // Auto-submit on 4-digit fill
  const handleChange = (v) => {
    const digits = v.replace(/\D/g, "").slice(0, 4);
    setValue(digits);
    if (digits.length === 4) doSubmit(digits);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel && onCancel();
      }}
    >
      <DialogContent
        data-testid="pin-modal"
        className="max-w-[92vw] border-white/10 bg-[#0a0a0c] text-text-main sm:max-w-sm"
      >
        <DialogHeader className="items-center text-center">
          <div
            className="mb-2 flex h-11 w-11 items-center justify-center rounded-full border border-accent/40 bg-accent/10 text-accent"
            aria-hidden
          >
            {isLocked ? (
              <LockKeyhole className="h-5 w-5" />
            ) : (
              <ShieldCheck className="h-5 w-5" />
            )}
          </div>
          <DialogTitle className="font-display text-lg font-bold tracking-tight">
            Enter PIN
          </DialogTitle>
          <DialogDescription className="font-mono-safe text-[11px] text-text-muted">
            Required for any change (add · edit · delete · +1 · undo).
            Reads stay public.
          </DialogDescription>
        </DialogHeader>

        <motion.div
          key={shakeKeyRef.current}
          animate={
            messageKind === "error" && !isLocked
              ? { x: [0, -8, 8, -6, 6, -3, 3, 0] }
              : { x: 0 }
          }
          transition={{ duration: 0.42, ease: "easeInOut" }}
          className="flex flex-col items-center gap-3 py-2"
        >
          <InputOTP
            value={value}
            onChange={handleChange}
            maxLength={4}
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus
            disabled={submitting || isLocked}
            data-testid="pin-input"
            containerClassName="gap-2"
          >
            <InputOTPGroup className="gap-2">
              {[0, 1, 2, 3].map((i) => (
                <InputOTPSlot
                  key={i}
                  index={i}
                  data-testid={`pin-slot-${i}`}
                  className="h-12 w-12 rounded-md border border-white/15 bg-black/40 text-lg font-bold text-text-main data-[active=true]:border-accent data-[active=true]:ring-2 data-[active=true]:ring-accent/40"
                />
              ))}
            </InputOTPGroup>
          </InputOTP>

          {isLocked ? (
            <p
              data-testid="pin-lockout"
              className="font-mono-safe text-[11px] text-red-300"
            >
              Locked. Try again in {secondsLeft}s.
            </p>
          ) : message ? (
            <p
              data-testid="pin-message"
              className={
                "font-mono-safe text-[11px] " +
                (messageKind === "error" ? "text-red-300" : "text-text-muted")
              }
            >
              {message}
            </p>
          ) : (
            <p className="font-mono-safe text-[11px] text-text-muted/70">
              4-digit PIN
            </p>
          )}
        </motion.div>

        <div className="mt-1 flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onCancel && onCancel()}
            data-testid="pin-cancel"
            className="text-text-muted hover:text-text-main"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => doSubmit(value)}
            disabled={value.length !== 4 || submitting || isLocked}
            data-testid="pin-submit"
            className="border border-accent/40 bg-accent/20 text-accent hover:bg-accent/30"
          >
            {submitting ? "Verifying…" : "Unlock"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
